import { useCallback } from 'react';

import type { MPLPreApprovalOffer } from 'commonTypes/api/scenarios/CreditSelectionScreenContent';
import { removeUndefined } from 'commonUtils/removeUndefined';
import { RESTRICTION_FLAGS } from 'constants/offer';

import { sendSentryClientErrorOnce, useAppDispatch, useAppSelector } from 'shared/lib';
import { sendEventOfferSent, sendEventPaidClick } from 'shared/lib/sendGAEvents';
import type { TOffer } from 'shared/types/TRankingService';

import {
  addOffer,
  disableOffersByCategoryAndPledge,
  disableOffersFromSameBank,
  updateOfferData,
  updateOffersStatuses,
} from 'entities/offers';
import { calculateAvailableBankThunk } from 'entities/offers/model/thunks';
import { setMplPreApprovalOfferStatus } from 'entities/scenario';
import { selectIsFullDealPage } from 'entities/siteSettings';
import { useChannel } from 'entities/siteSettings/lib';
import { getStParentId, resolveStOrderIdThunk } from 'entities/st-tracking';
import { accountSelector } from 'entities/user';

import {
  useApplyOfferMutation,
  useSendApplicationV2Mutation,
  useSendReferralApplicationMutation,
} from '../model/applyOffer.query';

import { collectMplPreApprovalDataThunk } from './collectMplPreApprovalDataThunk';
import { collectOfferDataThunk } from './collectOfferDataThunk';
import { collectPreApprovalDataThunk } from './collectPreApprovalDataThunk';
import { getAffOfferIdFromUrl } from './getAffOfferIdFromUrl';
import type { TAdditionalData } from './offerToApplyOfferDataAdapter';

export const useSendOffer = (place?: string) => {
  const [applyOffer, { isLoading }] = useApplyOfferMutation();
  const [sendReferralApplication] = useSendReferralApplicationMutation();
  const [sendApplicationV2, { isLoading: isLoadingV2 }] = useSendApplicationV2Mutation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(accountSelector);
  const channel = useChannel();
  const isFullDealPage = useAppSelector(selectIsFullDealPage);

  const sendOffer = useCallback(
    async (offer: TOffer, other?: TAdditionalData) => {
      try {
        const stOrderId = await dispatch(resolveStOrderIdThunk(offer)).unwrap();
        const stParentId = getStParentId() || undefined;
        const data = dispatch(
          collectOfferDataThunk({
            offer,
            other: stOrderId ? { ...(other || {}), stOrderId, stParentId } : other,
          }),
        );

        sendEventOfferSent({
          calcId: offer.calcId,
          productId: offer.id,
          productName: offer.productName,
          organizationId: offer.bankId,
          organizationName: offer.bankName,
          position: offer.order,
          group: offer.group,
        });

        const result = await dispatch(
          calculateAvailableBankThunk({
            successCallback: async (availableBanks) => {
              return applyOffer({
                ...removeUndefined(data),
                other: { ...(data?.other ? removeUndefined(data?.other) : {}), availableBanks },
                client: {
                  channel,
                },
              }).unwrap();
            },
            ids: [offer.newId!],
          }),
        );

        dispatch(updateOfferData({ id: offer.id, status: result.status }));
        dispatch(
          setMplPreApprovalOfferStatus({
            productId: offer.id,
            bankId: offer.bankId,
            loansStatus: result.loansStatus,
          }),
        );
        // для экрана MPL не отправлять оффер в Active
        if (!isFullDealPage) {
          dispatch(addOffer({ ...offer, status: result.status, group: 'Active' }));
        }

        if (offer.additionalInfo.restrictionFlags?.includes(RESTRICTION_FLAGS.PARTIAL_RESTRICT_BY_TYPE)) {
          dispatch(
            disableOffersByCategoryAndPledge({
              offerId: offer.id,
              bankId: offer.bankId,
              category: offer.category,
              rawPledgeList: offer.additionalInfo.rawPledgeList || [],
            }),
          );
          return;
        }

        dispatch(disableOffersFromSameBank({ offerId: offer.id, bankId: offer.bankId }));
      } catch (error) {
        sendSentryClientErrorOnce(`sendOffer ${place}`, error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user],
  );

  const sendReferralOffer = useCallback(
    async (offer: TOffer, other: TAdditionalData & { uuid: string; referralUrl?: string }) => {
      const { referralUrl, uuid } = other || {};
      try {
        const affOfferId = getAffOfferIdFromUrl(referralUrl);
        const data = dispatch(
          collectOfferDataThunk({
            offer,
            other: {
              ...other,
              ...(affOfferId ? { affOfferId } : {}),
            },
          }),
        );

        sendEventPaidClick({
          calcId: offer.calcId,
          productId: offer.id,
          productName: offer.productName,
          organizationId: offer.bankId,
          organizationName: offer.bankName,
          position: offer.order,
          group: offer.group,
        });

        await dispatch(
          calculateAvailableBankThunk({
            successCallback: async (availableBanks) => {
              return sendReferralApplication({
                ...removeUndefined(data),
                other: { ...(data?.other ? removeUndefined(data?.other) : {}), availableBanks },
                uuid,
                preApprovalOfferId: offer?.preApprovalOffer?.id,
                client: {
                  channel,
                },
              }).unwrap();
            },
            ids: [offer.newId!],
          }),
        );
      } catch (error) {
        sendSentryClientErrorOnce(`sendReferralOffer ${place}`, error);
        throw error;
      }
    },
    [dispatch, sendReferralApplication, channel, place],
  );

  const sendPreApprovalOffer = useCallback(
    async (offer: TOffer, other: TAdditionalData & { uuid: string; referralUrl?: string }) => {
      const { referralUrl, uuid } = other || {};
      try {
        const affOfferId = getAffOfferIdFromUrl(referralUrl);
        const stParentId = getStParentId() || undefined;
        const stOrderId = referralUrl ? null : await dispatch(resolveStOrderIdThunk(offer)).unwrap();
        const data = dispatch(
          collectPreApprovalDataThunk({
            offer,
            other: {
              ...other,
              ...(affOfferId ? { affOfferId } : {}),
              ...(stOrderId ? { stOrderId, stParentId } : {}),
            },
          }),
        );

        sendEventOfferSent({
          calcId: offer.calcId,
          productId: offer.id,
          productName: offer.productName,
          organizationId: offer.bankId,
          organizationName: offer.bankName,
          position: offer.order,
          group: offer.group,
        });

        const result = await dispatch(
          calculateAvailableBankThunk({
            successCallback: async (availableBanks) => {
              return sendApplicationV2({
                ...removeUndefined(data),
                other: {
                  ...(data?.other ? removeUndefined(data?.other) : {}),
                  availableBanks,
                  preApprovalOfferId: offer?.preApprovalOffer?.id,
                  uuid,
                },
                client: {
                  channel,
                },
              }).unwrap();
            },
            ids: [offer.newId!],
          }),
        );

        dispatch(
          setMplPreApprovalOfferStatus({
            productId: offer.id,
            bankId: offer.bankId,
            loansStatus: result.status,
          }),
        );
        dispatch(updateOfferData({ id: offer.id, status: result.offerStatus, loansStatus: result.status }));

        if (offer.additionalInfo.restrictionFlags?.includes(RESTRICTION_FLAGS.PARTIAL_RESTRICT_BY_TYPE)) {
          dispatch(
            disableOffersByCategoryAndPledge({
              offerId: offer.id,
              bankId: offer.bankId,
              category: offer.category,
              rawPledgeList: offer.additionalInfo.rawPledgeList || [],
            }),
          );
          return;
        }
        dispatch(disableOffersFromSameBank({ offerId: offer.id, bankId: offer.bankId }));
      } catch (error) {
        sendSentryClientErrorOnce(`sendPreApprovalOffer  ${place}`, error);
      }
    },
    [dispatch, sendApplicationV2, channel, place],
  );

  const sendMplPreApprovalOffer = useCallback(
    async (offer: MPLPreApprovalOffer, other: TAdditionalData & { uuid: string; referralUrl?: string }) => {
      const { referralUrl, uuid } = other || {};
      try {
        const affOfferId = getAffOfferIdFromUrl(referralUrl);
        const data = dispatch(
          collectMplPreApprovalDataThunk({
            offer,
            other: {
              ...other,
              ...(affOfferId ? { affOfferId } : {}),
            },
          }),
        );

        sendEventOfferSent({
          // todo разобраться с calcId
          calcId: '',
          productId: offer.productId,
          productName: offer.productName,
          organizationId: offer.organizationNewId,
          organizationName: offer.organizationName,
          position: offer.order,
          group: 'Online',
        });

        const result = await dispatch(
          calculateAvailableBankThunk({
            successCallback: async (availableBanks) => {
              return sendApplicationV2({
                ...removeUndefined(data),
                other: {
                  ...(data?.other ? removeUndefined(data?.other) : {}),
                  availableBanks,
                  preApprovalOfferId: offer?.preApprovalOfferId,
                  uuid,
                },
                client: {
                  channel,
                },
              }).unwrap();
            },
            ids: [offer.organizationNewId],
          }),
        );

        dispatch(
          setMplPreApprovalOfferStatus({
            productId: offer.productId,
            bankId: offer.organizationNewId,
            loansStatus: result.status,
          }),
        );

        dispatch(updateOfferData({ id: offer.productId, status: result.offerStatus, loansStatus: result.status }));
        dispatch(
          updateOffersStatuses({
            productId: offer.productId,
            bankId: offer.organizationNewId,
            status: result.offerStatus,
          }),
        );
        dispatch(
          disableOffersFromSameBank({
            offerId: offer.productId,
            bankId: offer.organizationNewId,
          }),
        );
      } catch (error) {
        sendSentryClientErrorOnce(`sendMplPreApprovalOffer  ${place}`, error);
      }
    },
    [dispatch, sendApplicationV2, channel, place],
  );

  return {
    sendOffer,
    isLoading: isLoading || isLoadingV2,
    sendReferralOffer,
    sendPreApprovalOffer,
    sendMplPreApprovalOffer,
  };
};
